import * as React from 'react'
import {
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  SelectStateContext,
  Button as AriaButton,
  Popover,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Header,
  Label,
  Text,
  FieldError,
  type SelectProps as AriaSelectProps,
  type ListBoxItemProps as AriaListBoxItemProps,
} from 'react-aria-components'
import { CaretDown, Check, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Avatar } from './avatar'

/**
 * Select — a single-select dropdown (button trigger → popover listbox), built on
 * react-aria-components. Compound API so items can hold rich content, group into
 * sections, and wire label/description/error with correct ARIA automatically.
 * For search or multi-select, use `Combobox` (this is single-select by design).
 *
 *   <Select placeholder="Pick a fruit">
 *     <SelectLabel>Fruit</SelectLabel>
 *     <SelectTrigger />
 *     <SelectContent>
 *       <SelectItem id="apple">Apple</SelectItem>
 *       <SelectSection><SelectHeader>Citrus</SelectHeader>
 *         <SelectItem id="orange">Orange</SelectItem>
 *       </SelectSection>
 *     </SelectContent>
 *   </Select>
 *
 * Trigger reuses the `Input` box look (basil focus ring, `sm|md|lg`); the menu
 * is the DS panel; the selected item gets a light-basil row + ✓. Boolean props
 * follow DS naming and map to react-aria's `is*`.
 */

type SelectSize = 'sm' | 'md' | 'lg'
type SelectStatus = 'default' | 'error' | 'success'

const SelectContext = React.createContext<{
  size: SelectSize
  status: SelectStatus
  disabled: boolean
}>({ size: 'md', status: 'default', disabled: false })
const useSelectCtx = () => React.useContext(SelectContext)

const triggerSize: Record<SelectSize, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-[0.75rem] text-sm',
  lg: 'h-10 px-3.5 text-base',
}

const triggerStatus: Record<SelectStatus, string> = {
  default:
    '!border-fbui-gray-300 group-data-[open]:!border-fbui-basil-700 group-data-[open]:ring-fbui-ring-brand data-[focus-visible]:!border-fbui-basil-700 data-[focus-visible]:ring-fbui-ring-brand',
  error:
    '!border-fbui-red-500 group-data-[open]:!border-fbui-red-600 group-data-[open]:ring-fbui-ring-error data-[focus-visible]:!border-fbui-red-600 data-[focus-visible]:ring-fbui-ring-error',
  success:
    '!border-fbui-green-500 group-data-[open]:!border-fbui-green-600 group-data-[open]:ring-fbui-ring-success data-[focus-visible]:!border-fbui-green-600 data-[focus-visible]:ring-fbui-ring-success',
}

const itemSize: Record<SelectSize, string> = {
  sm: 'gap-2 px-2.5 py-1.5 text-xs',
  md: 'gap-2.5 px-[0.75rem] py-2 text-sm',
  lg: 'gap-2.5 px-3.5 py-2.5 text-base',
}

const glyphSize: Record<SelectSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4',
}

// Leading-adornment slot: sizes plain SVG icons but lets richer content
// (e.g. an Avatar) keep its own dimensions, so it never overflows a fixed box
// and eat the gap after it.
const iconBox: Record<SelectSize, string> = {
  sm: '[&_svg]:size-3.5',
  md: '[&_svg]:size-4',
  lg: '[&_svg]:size-4',
}

// A full-size Avatar (sm = 32px) overwhelms the single-line trigger, so scale a
// leading Avatar down to fit the control height (menu rows keep the authored
// size). Plain SVG icons are sized by `iconBox` instead.
const triggerAvatarSize: Record<SelectSize, 'xs' | 'sm'> = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
}

// Right padding on the trigger that reserves room for the end adornments —
// the caret alone, or the caret plus a clear button when a value is selected.
const caretPad: Record<SelectSize, string> = {
  sm: 'pr-8',
  md: 'pr-9',
  lg: 'pr-9',
}
const clearPad: Record<SelectSize, string> = {
  sm: 'pr-[3.25rem]',
  md: 'pr-[3.5rem]',
  lg: 'pr-[3.5rem]',
}

export interface SelectProps extends Omit<
  AriaSelectProps<object>,
  'isDisabled' | 'isRequired' | 'isInvalid' | 'className' | 'children'
> {
  size?: SelectSize
  status?: SelectStatus
  disabled?: boolean
  required?: boolean
  invalid?: boolean
  className?: string
  children?: React.ReactNode
}

export const Select = ({
  size = 'md',
  status,
  disabled,
  required,
  invalid,
  className,
  children,
  ...props
}: SelectProps) => {
  const resolved: SelectStatus = invalid ? 'error' : (status ?? 'default')
  return (
    <SelectContext.Provider
      value={{ size, status: resolved, disabled: Boolean(disabled) }}
    >
      <AriaSelect
        isDisabled={disabled}
        isRequired={required}
        isInvalid={invalid}
        className={cn('group flex w-fit flex-col gap-1.5', className)}
        {...props}
      >
        {children}
      </AriaSelect>
    </SelectContext.Provider>
  )
}
Select.displayName = 'Select'

export interface SelectLabelProps extends React.ComponentProps<typeof Label> {
  /** Append a muted "(optional)" suffix — mirrors FieldLabel's `optional`. */
  optional?: boolean
}

export const SelectLabel = ({
  className,
  optional,
  children,
  ...props
}: SelectLabelProps) => {
  const { size } = useSelectCtx()
  return (
    <Label
      className={cn(
        'font-medium text-fbui-text-title group-data-[disabled]:text-fbui-text-disabled',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}
      {...props}
    >
      {children}
      {optional && (
        <span className="ml-1 font-normal text-fbui-gray-500 group-data-[disabled]:text-fbui-text-disabled">
          (optional)
        </span>
      )}
    </Label>
  )
}

export const SelectDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof Text>) => (
  <Text
    slot="description"
    className={cn('text-xs text-fbui-text-secondary', className)}
    {...props}
  />
)

export const SelectErrorMessage = ({
  className,
  ...props
}: React.ComponentProps<typeof FieldError>) => (
  <FieldError
    className={cn('text-xs text-fbui-red-700', className)}
    {...props}
  />
)

export interface SelectTriggerProps {
  className?: string
  children?: React.ReactNode
}

export const SelectTrigger = ({ className, children }: SelectTriggerProps) => {
  const { size, status, disabled } = useSelectCtx()
  const state = React.useContext(SelectStateContext)
  // Default trigger: show the clear button once a value is picked (custom
  // `children` opt out of the built-in adornments entirely).
  const showClear = !children && !disabled && state?.selectedKey != null
  return (
    <div className="group/trigger relative flex w-full min-w-44 items-center">
      <AriaButton
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-lg border bg-fbui-base-white text-left text-fbui-gray-800 outline-none transition-colors group-data-[open]:ring-2 data-[focus-visible]:ring-2 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50 data-[disabled]:text-fbui-text-disabled',
          triggerSize[size],
          triggerStatus[status],
          !children && (showClear ? clearPad[size] : caretPad[size]),
          className
        )}
      >
        {children ?? <SelectValue />}
      </AriaButton>
      {showClear && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={() => state?.setSelectedKey(null)}
          className="absolute right-7 flex size-5 items-center justify-center rounded text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
        >
          <X weight="bold" aria-hidden="true" className="size-3.5" />
        </button>
      )}
      {!children && (
        <CaretDown
          weight="bold"
          aria-hidden="true"
          className="pointer-events-none absolute right-3 size-3.5 text-fbui-gray-500"
        />
      )}
    </div>
  )
}

export const SelectValue = ({ className }: { className?: string }) => {
  const { size } = useSelectCtx()
  const state = React.useContext(SelectStateContext)
  return (
    <AriaSelectValue
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 truncate data-[placeholder]:text-fbui-gray-400',
        className
      )}
    >
      {({ selectedText, isPlaceholder, defaultChildren }) => {
        if (isPlaceholder) return defaultChildren
        // For items with a leading icon, echo only the icon + primary text on
        // one line — never the item's full (possibly multi-line) content, which
        // would overflow the single-line trigger. The icon lives on the selected
        // collection node's props. Plain items have no derivable `selectedText`
        // (their children are a render function), so fall back to the item's own
        // rendered content, which is already single-line.
        const icon = (
          state?.selectedItem?.props as { icon?: React.ReactNode } | undefined
        )?.icon
        if (!icon || !selectedText) return defaultChildren
        // Scale a leading Avatar down to fit the trigger; leave other nodes as-is.
        const adornment =
          React.isValidElement(icon) && icon.type === Avatar
            ? React.cloneElement(
                icon as React.ReactElement<{ size?: 'xs' | 'sm' }>,
                { size: triggerAvatarSize[size] }
              )
            : icon
        return (
          <>
            <span
              className={cn(
                'inline-flex shrink-0 items-center justify-center text-fbui-gray-500',
                iconBox[size]
              )}
            >
              {adornment}
            </span>
            <span className="truncate">{selectedText}</span>
          </>
        )
      }}
    </AriaSelectValue>
  )
}

export interface SelectContentProps {
  className?: string
  children?: React.ReactNode
}

export const SelectContent = ({ className, children }: SelectContentProps) => (
  <Popover
    offset={6}
    // Width on the popover (not the ListBox) so its p-1.5 sits inside the trigger
    // width — otherwise the panel is 12px wider than the trigger and spills right.
    className="z-50 w-[var(--trigger-width)] overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg outline-none"
  >
    <ListBox
      className={cn(
        'fbui-scroll max-h-72 w-full overflow-y-auto outline-none',
        className
      )}
    >
      {children}
    </ListBox>
  </Popover>
)

export interface SelectItemProps extends Omit<
  AriaListBoxItemProps,
  'children'
> {
  children?: React.ReactNode
  /** Leading icon/avatar. */
  icon?: React.ReactNode
}

// react-aria's ListBoxItem has no `icon` prop, but the collection captures
// whatever props we pass onto the node — and `SelectValue` reads it back from
// `selectedItem.props.icon` to echo the leading icon in the trigger. RAC filters
// the unknown prop out of the DOM, so this is safe.
// NOTE: this relies on RAC preserving unknown props on the collection node
// (`selectedItem.props`), which is undocumented. Verified against
// react-aria-components 1.19.0 (the pinned peer version); re-check the trigger
// icon if that pin is bumped.
const IconListBoxItem = ListBoxItem as React.ComponentType<
  AriaListBoxItemProps & { icon?: React.ReactNode }
>

export const SelectItem = ({
  className,
  children,
  icon,
  ...props
}: SelectItemProps) => {
  const { size } = useSelectCtx()
  return (
    <IconListBoxItem
      icon={icon}
      className={cn(
        'group/item flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none',
        'data-[focused]:bg-fbui-gray-100 data-[focused]:text-fbui-gray-900',
        'data-[selected]:bg-fbui-basil-50 data-[selected]:font-medium data-[selected]:text-fbui-basil-700',
        'data-[disabled]:pointer-events-none data-[disabled]:text-fbui-gray-300',
        itemSize[size],
        className
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          {icon && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center justify-center text-fbui-gray-500',
                iconBox[size]
              )}
            >
              {icon}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{children}</span>
          {renderProps.isSelected && (
            <Check
              weight="bold"
              aria-hidden="true"
              className={cn('shrink-0 text-fbui-basil-700', glyphSize[size])}
            />
          )}
        </>
      )}
    </IconListBoxItem>
  )
}

/**
 * Group of items with a `SelectHeader`. Sections after the first get a top
 * divider (a standalone separator element can't live in a react-aria
 * collection, so the rule lives on the section).
 */
export const SelectSection = ({
  className,
  ...props
}: React.ComponentProps<typeof ListBoxSection>) => (
  <ListBoxSection
    className={cn(
      '[&:not(:first-child)]:mt-1.5 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-fbui-gray-200 [&:not(:first-child)]:pt-1.5',
      className
    )}
    {...props}
  />
)

export const SelectHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof Header>) => (
  <Header
    className={cn(
      'px-[0.75rem] pb-1 pt-2 text-xs font-medium text-fbui-gray-500',
      className
    )}
    {...props}
  />
)
